/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */

import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '../../ui/dialog';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '../../ui/form';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import Icons from '../../../icons/icons';
import { CustomButton } from '../../common/custom-button';
import { formatEvalLabel } from '../../../lib/utils';
import { EvalResults } from '../../../types/api';
import { useLanguage } from '../../../providers/LanguageProvider';
import EnhancedCreateSelect from '../../combobox/enhanced-create-select';

interface EvalFeedbackModalProps {
    evalObj: EvalResults | undefined;
    templateEnums?: string[];
    templateLoading?: boolean;
    onClose: () => void;
    handleUserFeedback: (criteria: string, value: string, explanation: string) => void;
}

const EvalFeedbackModal = ({
    onClose,
    evalObj,
    templateEnums,
    templateLoading,
    handleUserFeedback,
}: EvalFeedbackModalProps) => {
    const defaultCriteria = evalObj?.eval_name;
    const defaultValue = evalObj?.eval_result?.label;

    const { messages } = useLanguage();
    const evaluationLang = (messages as any)?.Evaluation;

    /**
     * Define the validation schema for the form using zod.
     */
    const TEMPLATE_NAME_REGEX = /^(?!_)(?!.*__)[A-Za-z0-9_]{1,50}(?<!_)$/;
    const TEMPLATE_NAME_ERROR = evaluationLang?.eval_custom_label_condition;
    const CRITERIA_ERROR = evaluationLang?.eval_criteria_required;
    const VALUE_ERROR = evaluationLang?.eval_value_required;
    const EXPLANATION_ERROR = evaluationLang?.eval_explanation_required;

    type FormData = z.infer<typeof formSchema>;

    const formSchema = z.object({
        criteria: z
        .string()
        .min(1, { message: CRITERIA_ERROR })
        .max(50, { message: TEMPLATE_NAME_ERROR })
        .regex(TEMPLATE_NAME_REGEX, { message: TEMPLATE_NAME_ERROR }),
        value: z
        .string()
        .min(1, { message: VALUE_ERROR })
        .max(50, { message: TEMPLATE_NAME_ERROR })
        .regex(TEMPLATE_NAME_REGEX, { message: TEMPLATE_NAME_ERROR }),
        explanation: z.string().min(1, { message: EXPLANATION_ERROR }),
    });

    const form = useForm<FormData>({
        resolver: zodResolver(formSchema),
        mode: 'onChange',
        defaultValues: {
            criteria: defaultCriteria,
            value: defaultValue,
            explanation: '',
        },
    });

    const { formState: { isValid } } = form;

    /**
     * Reset the form values when the modal is opened or when the evalObj changes.
     */
    useEffect(() => {
        form.reset({
            criteria: defaultCriteria,
            value: defaultValue,
            explanation: '',
        });
    }, [defaultCriteria, defaultValue, form]);

    /**
     * Handle selecting a value from the value list, or setting a custom typed value.
     * @param value The selected/typed value.
     */
    const handleSelectValue = (value: string) => {
        form.setValue('value', value, {
            shouldValidate: true,
            shouldDirty: true,
            shouldTouch: true,
        });
    };

    /**
     * Handles creating a new value when the user presses enter or clicks the create button.
     */
   const handleCreateNewValue = (typedValue: string) => {
        const nextValue = typedValue.trim();
        if (!nextValue) return;
        handleSelectValue(nextValue);
    };

    /**
     * Handles form submission when the user clicks the save button.
     * @param values The form values.
     */
    const onSubmit = (values: FormData) => {
       handleUserFeedback(values.criteria, values.value, values.explanation);
       onClose();
    };

    const normalizedEnums = (templateEnums ?? []).map((item) => ({
        value: item.trim(),
        label: formatEvalLabel(item),
    })); 

    const isCustomEval = Boolean(evalObj?.eval_id?.startsWith('custom_evaluation'));

    return (
        <Dialog open={true} onOpenChange={onClose}>
            <DialogContent
                className="max-w-[700px] border-link-active bg-gradient-sky-dark p-8"
                aria-describedby={undefined}
                onInteractOutside={(e) => {
                    e.preventDefault();
                }}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()} 
            >
                <DialogHeader>
                    <DialogTitle className="border-b border-line-primary pb-4 text-2xl text-white">
                        {evaluationLang?.provide_feedback}
                    </DialogTitle>
                </DialogHeader>

                <Form {...form}>
                    <form className="space-y-6" onSubmit={form.handleSubmit(onSubmit)}>
                        {/* Criteria field */}
                        <FormField
                            control={form.control}
                            name="criteria"
                            render={({ field }) => (
                            <FormItem>
                                <FormLabel className="text-sm">{evaluationLang?.criteria}</FormLabel>
                                <FormControl>
                                    <div className="relative">
                                        <Input
                                            placeholder="Select criteria"
                                            className="h-[42px] cursor-not-allowed rounded-sm border-form-control-border-rest pr-10 capitalize"
                                            value={formatEvalLabel(field.value)}
                                            disabled
                                            readOnly
                                        />
                                        <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                                            {Icons.ChevronDownIcon(
                                                'size-5 text-form-input-disabled'
                                            )}
                                        </div>
                                    </div>
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                            )}
                        />

                        {/* Value field */}
                        <FormField
                            control={form.control}
                            name="value"
                            render={({ field }) => (
                            <FormItem>
                                <FormLabel className="text-sm">{evaluationLang?.select_value}</FormLabel>
                                <FormControl>
                                    <EnhancedCreateSelect
                                        key={'value-select'}
                                        value={formatEvalLabel(field.value ?? '')}
                                        options={normalizedEnums}
                                        isLoading={templateLoading}
                                        disabled={!field.value}
                                        onValueChange={(value) => {
                                            field.onChange(value);
                                        }}
                                        onCreateNewValue={handleCreateNewValue}
                                        showCreate={isCustomEval}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                            )}
                        />

                        {/* Explanation field */}
                        <FormField
                            control={form.control}
                            name="explanation"
                            render={({ field }) => (
                                <FormItem className="mt-5">
                                    <FormLabel>{evaluationLang?.explanation}</FormLabel>
                                    <FormControl>
                                        <Textarea
                                            placeholder="Explain why this change is required"
                                            {...field}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        {/* Actions buttons */}
                        <div className="mb-10 flex gap-4 border-t border-line-primary pt-8">
                            <CustomButton
                                type="submit"
                                customSize="regular"
                                customVariant="primary"
                                disabled={!isValid}
                            >
                                {evaluationLang?.save}
                            </CustomButton>

                            <CustomButton
                                type="button"
                                customSize="regular"
                                customVariant="secondary"
                                onClick={onClose}
                            >
                                {evaluationLang?.cancel}
                            </CustomButton>
                        </div>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    );
};

export default EvalFeedbackModal;